import { postmortemApiEndpoint } from "../config";
import { useFetchWithRetry } from "../Utils/fetchWithRetry";
import { logError, logInfo } from "../Utils/loggers";
import { PutWithRetry } from "../Utils/putWithRetry";
import { cachedRequest } from "../Utils/cachedRequest";
import { useMsal } from "@azure/msal-react";
import { useQueryClient } from "react-query";
import { fetchToken } from "./fetchTokenService";
import { loginRequest } from "../auth/authConfig";
import { getConsumerKey } from "./maerskApiTokenService";

export const PostmortemServices = () => {
  const { fetchWithRetry } = useFetchWithRetry();
  const { putWithRetry } = PutWithRetry();
  const { instance, accounts } = useMsal();
  const queryClient = useQueryClient();
  // Writes call this so cached reads of the same postmortem do not show stale data.
  const invalidatePostmortemCaches = () =>
    Promise.all([
      queryClient.invalidateQueries(["postmortem"]),
      queryClient.invalidateQueries(["enhancement"]),
      queryClient.invalidateQueries(["teams"]),
    ]);

  const fetchPaginatedPostMortemData = async (apiEndpoint, pg, pgSize, filters = {}) => {
    logInfo(
      "fetchPaginatedPostMortemData",
      "fetching paginated postmortem data"
    );
    try {
      const params = new URLSearchParams({ page: pg - 1, size: pgSize });
      if (filters.priority?.length) params.set("priority", filters.priority.join(","));
      if (filters.status?.length) params.set("status", filters.status.join(","));
      if (filters.ci) params.set("ci", filters.ci);
      if (filters.causedByCi) params.set("causedByCi", filters.causedByCi);
      if (filters.portfolio) params.set("portfolio", filters.portfolio);
      if (filters.incidentId) params.set("incidentId", filters.incidentId);
      if (filters.sortDirection) params.set("sortDirection", filters.sortDirection);
      const url = apiEndpoint + `/fetch-paginated-post-mortem-data?${params.toString()}`;

      const data = await fetchWithRetry(url);
      logInfo(
        "fetchPaginatedPostMortemData",
        "fetched paginated postmortem data successfully"
      );
      return data;
    } catch (error) {
      logError(
        "fetchPaginatedPostMortemData",
        "error fetching paginated postmortem data",
        error
      );
      throw error;
    }
  };

  const fetchPostmortemById = async (apiEndpoint, postmortemID) => {
    if (postmortemID) {
      logInfo("fetchPostmortemById", "fetching postmortem data by id");
      const isEditLock = postmortemID.includes("edit");
      const [baseId, query = ""] = postmortemID.split("?");
      const load = async () => {
        try {
          const url =
            apiEndpoint + `/fetch-postmortem-data-by-number/${postmortemID}`;

          const data = await fetchWithRetry(url);
          logInfo(
            "fetchPostmortemById",
            "fetched postmortem data by id successfully"
          );

          if (isEditLock) {
            data["postmortemData"]["lockedFields"] = data["lockedFields"];
          }

          return data;
        } catch (error) {
          logError(
            "fetchPostmortemById",
            "error fetching postmortem data by id",
            error
          );

          throw error;
        }
      };

      // Edit requests take a lock and always go to the backend. Other reads are shared and cached.
      if (isEditLock) return load();
      const cached = await cachedRequest(
        queryClient,
        ["postmortem", baseId, query],
        load
      );
      return JSON.parse(JSON.stringify(cached));
    }
  };

  const fetchAllPostmortems = async (
    userIsAdmin,
    userAccessData,
    callingFor
  ) => {
    logInfo("fetchAllPostmortems", "fetching all postmortems");
    try {
      const url = postmortemApiEndpoint + "/fetch-all-post-mortem-data";

      const data = await fetchWithRetry(url, callingFor);
      logInfo("fetchAllPostmortems", "fetched all postmortems successfully");

      // if(callingFor === "archived"){
      return data;
      // }else{
      //   return userIsAdmin
      //     ? data
      //     : data.filter((data) =>
      //         userAccessData?.some(
      //           (userAccessData) => userAccessData.postMortemId === data.customID
      //         )
      //       );
      // }
    } catch (error) {
      logError("fetchAllPostmortems", "error fetching all postmortems", error);

      throw error;
    }
  };

  // Lightweight endpoint for NavBar dropdown - 95% faster than fetchAllPostmortems
  const fetchIncidentDropdownData = async () => {
    logInfo("fetchIncidentDropdownData", "fetching incident dropdown data");
    try {
      const url = postmortemApiEndpoint + "/fetch-incident-dropdown-data";
      const data = await fetchWithRetry(url, "incident-dropdown");
      logInfo("fetchIncidentDropdownData", "fetched incident dropdown data successfully");
      return data;
    } catch (error) {
      logError("fetchIncidentDropdownData", "error fetching incident dropdown data", error);
      throw error;
    }
  };

  // Lightweight endpoint for Archived table - 70% faster than fetchAllPostmortems
  const fetchArchivedTableData = async () => {
    logInfo("fetchArchivedTableData", "fetching archived table data");
    try {
      const url = postmortemApiEndpoint + "/fetch-archived-table-data";
      const data = await fetchWithRetry(url, "archived-table");
      logInfo("fetchArchivedTableData", "fetched archived table data successfully");
      return data;
    } catch (error) {
      logError("fetchArchivedTableData", "error fetching archived table data", error);
      throw error;
    }
  };

  const fetchCollaborators = async (apiEndpoint, postmortemID) => {
    logInfo("fetchCollaborators", "fetching collaborators");
    try {
      const url = apiEndpoint + `/teams/${postmortemID}`;
      const data = await cachedRequest(queryClient, ["teams", postmortemID], () =>
        fetchWithRetry(url)
      );
      return JSON.parse(JSON.stringify(data));
    } catch (error) {
      logError("fetchCollaborators", "error fetching collaborators", error);
      throw error;
    }
  };

  const fetchPostmortemByIdAndCollaborators = async (
    apiEndpoint,
    postmortemID
  ) => {
    logInfo(
      "fetchPostmortemByIdAndCollaborators",
      "fetching postmortem data by id and collaborators"
    );
    try {
      let postmortemObject = await fetchPostmortemById(
        apiEndpoint,
        postmortemID
      );
      logInfo(
        "fetchPostmortemByIdAndCollaborators",
        "fetched postmortem data by id  successfully"
      );
      let collaborators = await fetchCollaborators(apiEndpoint, postmortemID);
      logInfo(
        "fetchPostmortemByIdAndCollaborators",
        "fetched collaborators successfully"
      );
      return {
        postmortemObject: postmortemObject,
        collaborators: collaborators.members,
        postmortemTeamId: collaborators.teamId,
      };
    } catch (error) {
      logError(
        "fetchPostmortemByIdAndCollaborators",
        "error fetching postmortem data by id and collaborators",
        error
      );

      throw error;
    }
  };

  const postPostMortemData = async (
    apiEndpoint,
    incidentID,
    postmortemData,
    headers = {}
  ) => {
    try {
      logInfo("postPostMortemData", "posting postmortem data");
      const url = apiEndpoint + "/update-all-data?incidentNumber=" + incidentID;
      // Always identify the acting user to the backend for authorization,
      // regardless of what (if anything) the caller passed as headers.
      const userEmail = accounts[0]?.username || accounts[0]?.name || "unknown";
      const safeHeaders = headers && typeof headers === "object" ? headers : {};
      const data = await putWithRetry(url, postmortemData, { "X-User": userEmail, ...safeHeaders });
      invalidatePostmortemCaches();
      logInfo("postPostMortemData", "posted postmortem data successfully");
      return data;
    } catch (error) {
      logError("postPostMortemData", "error posting postmortem data", error);
      throw error;
    }
  };
  const updateIncompleteStatus = async (
    apiEndpoint,
    incidentID,
    postmortemData
  ) => {
    try {
      logInfo("updateIncompleteStatus", "updating incomplete status");
      const url = apiEndpoint + `/update-incomplete-status/${incidentID}`;
      const data = await putWithRetry(url, postmortemData, {});
      invalidatePostmortemCaches();
      logInfo(
        "updateIncompleteStatus",
        "updated incomplete status successfully"
      );
      return data;
    } catch (error) {
      logError(
        "updateIncompleteStatus",
        "error updating incomplete status",
        error
      );
      throw error;
    }
  };

  const postAuditData = async (apiEndpoint, auditData) => {
    try {
      logInfo("postAuditData", "posting audit data");
      const url = apiEndpoint + `/audit-data`;
      const data = await putWithRetry(url, auditData, {});
      logInfo("postAuditData", "posted audit data successfully");
      return data;
    } catch (error) {
      logError("postAuditData", "error posting audit data", error);
      throw error;
    }
  };

  const getAuditData = async (apiEndpoint, incidentID) => {
    try {
      logInfo("getAuditData", "fetching audit data");
      const url = apiEndpoint + `/audit-data/${incidentID}`;
      const data = await fetchWithRetry(url);
      logInfo("getAuditData", "fetched audit data successfully");
      return data;
    } catch (error) {
      logError("getAuditData", "error fetching audit data", error);
      throw error;
    }
  };

  const fetchEnhancement = async (postMortemId) => {
    try {
      const url = `${postmortemApiEndpoint}/postmortem-enhancements/${postMortemId}`;
      const data = await cachedRequest(queryClient, ["enhancement", postMortemId], () =>
        fetchWithRetry(url)
      );
      return JSON.parse(JSON.stringify(data));
    } catch (error) {
      logError('fetchEnhancement', 'error fetching enhancement', error);
      throw error;
    }
  };

  const _getAuthHeaders = async () => {
    const tokenResponse = await fetchToken(instance, loginRequest, accounts);
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${tokenResponse.idToken}`,
      'consumer-key': getConsumerKey(),
    };
  };

  const startPause = async (postMortemId, incidentId, startedBy, reason) => {
    try {
      const url = `${postmortemApiEndpoint}/postmortem-enhancements/${postMortemId}/pause/start`;
      const headers = await _getAuthHeaders();
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ incidentId, startedBy, reason })
      });
      if (!response.ok) throw new Error('Failed to start pause');
      invalidatePostmortemCaches();
      return await response.json();
    } catch (error) {
      logError('startPause', 'error starting pause', error);
      throw error;
    }
  };

  const stopPause = async (postMortemId, stoppedBy) => {
    try {
      const url = `${postmortemApiEndpoint}/postmortem-enhancements/${postMortemId}/pause/stop`;
      const headers = await _getAuthHeaders();
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ stoppedBy })
      });
      if (!response.ok) throw new Error('Failed to stop pause');
      invalidatePostmortemCaches();
      return await response.json();
    } catch (error) {
      logError('stopPause', 'error stopping pause', error);
      throw error;
    }
  };

  const getRcaQuality = async (apiEndpoint, incidentNumber, forceRefresh = false) => {
    try {
      const url = `${apiEndpoint}/check-rca-quality/${incidentNumber}${forceRefresh ? "?forceRefresh=true" : ""}`;
      const data = await fetchWithRetry(url, "rcaQuality");
      return data;
    } catch (error) {
      throw new Error(`Error fetching RCA quality data: ${error.message}`);
    }
  };

  // Apigee sometimes serves "No API path found" for a path that is live elsewhere
  // (stale proxy revision on some gateway nodes/regions); a short retry rides over it
  // instead of surfacing a hard failure to the user.
  const isRetryableGatewayRoutingError = (status, body) => {
    if (status !== 400) return false;
    const text = typeof body === "string" ? body : JSON.stringify(body || "");
    return /No API path found/i.test(text);
  };

  const withGatewayRoutingRetry = async (makeRequest, retries = 2, delay = 800) => {
    try {
      return await makeRequest();
    } catch (requestError) {
      const status = requestError.response?.status ?? requestError.status;
      const body = requestError.response?.data ?? requestError.body;
      if (retries > 0 && isRetryableGatewayRoutingError(status, body)) {
        await new Promise((resolve) => setTimeout(resolve, delay));
        return withGatewayRoutingRetry(makeRequest, retries - 1, delay * 2);
      }
      throw requestError;
    }
  };

  const getRecurrenceAnalysisStatus = async (incidentId) => {
    return withGatewayRoutingRetry(() =>
      fetchWithRetry(
        `${postmortemApiEndpoint}/recurrence-analysis/${incidentId}/status`,
        "recurrenceAnalysisStatus"
      )
    );
  };

  const triggerRecurrenceAnalysis = async (incidentId) => {
    return withGatewayRoutingRetry(async () => {
      const headers = await _getAuthHeaders();
      const response = await fetch(
        `${postmortemApiEndpoint}/recurrence-analysis/${incidentId}/trigger`,
        { method: "POST", headers }
      );
      if (!response.ok && response.status !== 409) {
        let body = null;
        try {
          body = await response.clone().json();
        } catch (parseError) {
          // response wasn't JSON; leave body null
        }
        const error = new Error(`Failed to start recurrence analysis (${response.status})`);
        error.status = response.status;
        error.body = body;
        throw error;
      }
      return response.json();
    });
  };

  const recordRecurrenceDecision = async (incidentId, decision, selectedIncidentId) => {
    const headers = await _getAuthHeaders();
    headers["X-User"] = accounts[0]?.username || accounts[0]?.name || "unknown";
    const response = await fetch(
      `${postmortemApiEndpoint}/recurrence-analysis/${incidentId}/decision`,
      {
        method: "PUT",
        headers,
        body: JSON.stringify({ decision, selectedIncidentId }),
      }
    );
    if (!response.ok) {
      throw new Error(`Failed to save recurrence decision (${response.status})`);
    }
    return response.json();
  };

  // Admin-only; the server re-checks the caller, so this is not the access control.
  const getRecurrenceMetrics = async () => {
    const headers = await _getAuthHeaders();
    const userEmail = accounts[0]?.username || accounts[0]?.name || "";
    const response = await fetch(
      `${postmortemApiEndpoint}/recurrence-analysis/metrics?userEmail=${encodeURIComponent(userEmail)}`,
      { method: "GET", headers }
    );
    if (!response.ok) {
      throw new Error(`Failed to load recurrence metrics (${response.status})`);
    }
    return response.json();
  };

  // Per-match accept/reject (or manual-add) feedback, independent of the single
  // whole-analysis decision above. Used to measure how well the suggestions perform.
  const recordRecurrenceFeedback = async (
    incidentId,
    { matchIncidentId, matchPostMortemId, verdict, reason, note, source }
  ) => {
    const headers = await _getAuthHeaders();
    headers["X-User"] = accounts[0]?.username || accounts[0]?.name || "unknown";
    const response = await fetch(
      `${postmortemApiEndpoint}/recurrence-analysis/${incidentId}/feedback`,
      {
        method: "PUT",
        headers,
        body: JSON.stringify({ matchIncidentId, matchPostMortemId, verdict, reason, note, source }),
      }
    );
    if (!response.ok) {
      throw new Error(`Failed to save recurrence feedback (${response.status})`);
    }
    return response.json();
  };

  // Semantic accuracy API service
  const getSemanticAccuracy = async (apiEndpoint, incidentNumber) => {
    try {
      const data = await fetchWithRetry(
        `${apiEndpoint}/find-accuracy/${incidentNumber}`,
        "semanticAccuracy"
      );
      return data;
    } catch (error) {
      throw new Error(
        `Error fetching semantic accuracy data: ${error.message}`
      );
    }
  };

  // Manual RCA generation API service
  const generateRCAOnDemand = async (incidentId, triggeredBy) => {
    try {
      logInfo("generateRCAOnDemand", `Triggering manual RCA generation for ${incidentId} by ${triggeredBy}`);
      
      // Use FREE_TEXT_API_ENDPOINT for direct backend access (same pattern as freeTextService)
      const freeTextApiBase = process.env.REACT_APP_FREE_TEXT_API_ENDPOINT;
      const url = `${freeTextApiBase}/api/postmortem/${incidentId}/generate-rca-on-demand?triggeredBy=${encodeURIComponent(triggeredBy)}`;
      
      const tokenResponse = await fetchToken(instance, loginRequest, accounts);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokenResponse.idToken}`,
          'consumer-key': getConsumerKey(),
        }
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle different error scenarios
        if (response.status === 400) {
          throw new Error(data.error || 'RCA generation only allowed for postmortems in NEW state');
        } else if (response.status === 409) {
          throw new Error(data.error || 'RCA generation already in progress for this incident');
        } else if (response.status === 404) {
          throw new Error(data.error || 'Postmortem not found');
        } else {
          throw new Error(data.error || 'Failed to initiate RCA generation');
        }
      }

      logInfo("generateRCAOnDemand", "Manual RCA generation initiated successfully");
      return data;
    } catch (error) {
      logError("generateRCAOnDemand", "Error triggering manual RCA generation", error);
      throw error;
    }
  };

  const updateClosedWithoutLTReviewStatus = async (
    apiEndpoint,
    incidentID,
    statusData
  ) => {
    try {
      logInfo("updateClosedWithoutLTReviewStatus", "updating closed without LT review status");
      const url = apiEndpoint + `/postmortem-enhancements/${incidentID}/close-without-lt-review`;
      const data = await putWithRetry(url, statusData, {});
      invalidatePostmortemCaches();
      logInfo(
        "updateClosedWithoutLTReviewStatus",
        "updated closed without TLT REVIEW status successfully"
      );
      return data;
    } catch (error) {
      logError(
        "updateClosedWithoutLTReviewStatus",
        "error updating closed without TLT REVIEW status",
        error
      );
      throw error;
    }
  };

  const updateEDSignOffStatus = async (
    apiEndpoint,
    incidentID,
    signOffData
  ) => {
    try {
      logInfo("updateEDSignOffStatus", "updating ED sign-off status");
      const url = apiEndpoint + `/postmortem-enhancements/${incidentID}/ed-signoff`;
      const data = await putWithRetry(url, signOffData, {});
      invalidatePostmortemCaches();
      logInfo(
        "updateEDSignOffStatus",
        "updated ED sign-off status successfully"
      );
      return data;
    } catch (error) {
      logError(
        "updateEDSignOffStatus",
        "error updating ED sign-off status",
        error
      );
      throw error;
    }
  };

  const updatePlatformOwner = async (
    apiEndpoint,
    postMortemId,
    platformOwnerEmail
  ) => {
    try {
      logInfo("updatePlatformOwner", "updating Platform Owner");
      const url = apiEndpoint + `/postmortem-enhancements/${postMortemId}/platform-owner`;
      const data = await putWithRetry(url, { platformOwnerEmail }, {});
      invalidatePostmortemCaches();
      logInfo(
        "updatePlatformOwner",
        "updated Platform Owner successfully"
      );
      return data;
    } catch (error) {
      logError(
        "updatePlatformOwner",
        "error updating Platform Owner",
        error
      );
      throw error;
    }
  };

  return {
    fetchPaginatedPostMortemData,
    fetchPostmortemById,
    fetchAllPostmortems,
    fetchIncidentDropdownData,
    fetchArchivedTableData,
    fetchPostmortemByIdAndCollaborators,
    fetchCollaborators,
    postPostMortemData,
    updateIncompleteStatus,
    updateClosedWithoutLTReviewStatus,
    updateEDSignOffStatus,
    updatePlatformOwner,
    postAuditData,
    getAuditData,
    fetchEnhancement,
    startPause,
    stopPause,
    getSemanticAccuracy,
    getRcaQuality,
    getRecurrenceAnalysisStatus,
    triggerRecurrenceAnalysis,
    recordRecurrenceDecision,
    recordRecurrenceFeedback,
    getRecurrenceMetrics,
    generateRCAOnDemand,
  };
};
